import type { MessageType } from "./Talk";
import type { UserTagType } from "./UserTagType";
import type {
  IDeliverySaveBodyCondition,
  IDeliverySaveBody,
  DeliveryType,
  IDeliverySaveBodyStep,
} from "~/interfaces/IDeliveryRepository";

export interface DeliveryEmail {
  subject: string;
  senderName: string;
  body: {
    json: string;
    html: string;
  };
}

export interface DeliveryDM {
  messages: {
    talkId: string;
    talkType: MessageType;
    contents: string;
    thumbnailImage: string;
    fileName: string;
    fileKey: string;
    fileUrl: string;
    note: string;
  }[];
  sendSourceUserId: string;
  sendDmPlanId: string;
}
// 配信
export class Delivery {
  constructor(
    public messageDeliveryid: number | null,
    public name: string,
    public deliveryType: DeliveryType,
    public isImmediateDelivery: boolean,
    public scheduleDateTime: string,
    public sentDateTime: string,
    public isSendMail: boolean,
    public isSendDm: boolean,
    public mailContents: DeliveryEmail,
    public dmContents: DeliveryDM,
    public status: string,
    public condition: {
      conditions: IDeliverySaveBodyCondition[];
    },
    public stepTrigger: UserTagType[],
    public steps: IDeliverySaveBodyStep[],
    public result?: {
      sendCount: number;
      readedMailCount: number;
      readedDmCount: number;
      clickMailCount: number;
      clickDmCount: number;
    },
    public statusCode?: number,
  ) {}
}

export const emptyDelivery = () =>
  new Delivery(
    0,
    "",
    "BULK",
    true,
    "",
    "",
    false,
    false,
    {
      subject: "",
      senderName: "",
      body: {
        json: "{}",
        html: "",
      },
    },
    {
      messages: [],
      sendSourceUserId: "",
      sendDmPlanId: "",
    },
    "RUNNING",
    {
      conditions: [
        {
          isAndCondition: true,
          isAndUserTag: true,
          isExistUserTag: true,
          userTagConditions: [],
        },
      ],
    },
    [],
    [],
    {
      sendCount: 0,
      readedMailCount: 0,
      readedDmCount: 0,
      clickMailCount: 0,
      clickDmCount: 0,
    },
  );

export const emptyStep = (options: Partial<IDeliverySaveBodyStep> = {}): IDeliverySaveBodyStep => {
  return {
    deliveryDaysLater: 1,
    deliveryTime: 10,
    isInstantDeliveryStep: true,
    dmContents: {
      sendDmPlanId: "",
      sendSourceUserId: "",
      messages: [],
    },
    mailContents: {
      subject: "",
      senderName: "",
      body: { json: "", html: "" },
    },
    isSendMail: true,
    isSendDm: false,
    stepName: "",
    stepOrder: 0,
    sendCount: 0,
    ...options,
  };
};

export const toDeliverySeed = (data: Delivery, options: Partial<IDeliverySaveBody> = {}): IDeliverySaveBody => {
  return {
    name: data.name,
    delivery_type: data.deliveryType,
    is_immediate_delivery: data.isImmediateDelivery,
    schedule_date_time: data.scheduleDateTime,
    is_send_dm: data.isSendDm,
    is_send_mail: data.isSendMail,
    mail_contents: {
      subject: data.mailContents.subject,
      sender_name: data.mailContents.senderName,
      body: data.mailContents.body,
    },
    dm_contents: {
      messages: (data.dmContents.messages || []).map(message => ({
        talk_type: message.talkType,
        contents: message.contents,
        thumbnail_image: message.thumbnailImage,
        file_name: message.fileName,
        file_key: message.fileKey,
        file_url: message.fileUrl,
        note: message.note,
      })),
      send_source_user_id: data.dmContents.sendSourceUserId,
      send_dm_plan_id: data.dmContents.sendDmPlanId,
    },
    status: data.status,
    condition: {
      conditions: (data.condition.conditions || []).map(condition => toDeliveryConditionSeed(condition)),
    },
    step_trigger: (data.stepTrigger || []).map(trigger => ({
      user_tag_type_id: trigger.userTagTypeId,
      user_tag_category: trigger.userTagCategory,
      user_tag_key1: trigger.userTagKey1,
      user_tag_name: trigger.userTagName,
      user_tag_key2: trigger.userTagKey2,
      user_tag_key3: trigger.userTagKey3,
    })),
    steps: data.steps.map((step, index) => {
      return {
        step_name: step.stepName,
        step_order: index + 1,
        delivery_days_later: step.deliveryDaysLater,
        delivery_time: step.deliveryTime,
        is_send_mail: true, // step.isSendMail, 暫定対応
        is_send_dm: step.isSendDm,
        is_instant_delivery_step: step.isInstantDeliveryStep,
        mail_contents: {
          subject: step.mailContents.subject,
          sender_name: step.mailContents.senderName,
          body: {
            json: step.mailContents.body.json,
            html: step.mailContents.body.html,
          },
        },
        dm_contents: {
          messages: (data.dmContents.messages || []).map(message => ({
            talk_type: message.talkType,
            contents: message.contents,
            thumbnail_image: message.thumbnailImage,
            file_name: message.fileName,
            file_key: message.fileKey,
            file_url: message.fileUrl,
            note: message.note,
          })),
          send_source_user_id: data.dmContents.sendSourceUserId,
          send_dm_plan_id: data.dmContents.sendDmPlanId,
        },
      };
    }),
    ...options,
  };
};

export const toDeliveryConditionSeed = (condition: IDeliverySaveBodyCondition) => {
  return {
    is_and_condition: condition.isAndCondition,
    is_and_user_tag: condition.isAndUserTag,
    is_exist_user_tag: condition.isExistUserTag,
    user_tag_conditions: (condition.userTagConditions || []).map(userTagCondition => ({
      user_tag_type_id: userTagCondition.userTagTypeId,
      user_tag_category: userTagCondition.userTagCategory,
      user_tag_key1: userTagCondition.userTagKey1,
      user_tag_name: userTagCondition.userTagName,
      user_tag_key2: userTagCondition.userTagKey2,
      user_tag_key3: userTagCondition.userTagKey3,
    })),
  };
};
